import React from 'react';
import { Button, IconButton, Box } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { useUnitOverride, uploadUnitAsset, updateUnitContent } from '../../services/unitOverridesService';

const PAGE_ID = 'm3_u1_x3_absorp';

const images = [
  { key: 'frost', src: '/assets/drawable/frost.jpeg', alt: 'Frost Symbol', label: 'Frost' }
];

const textBlocks = [
  {
    id: 'block_titel',
    heading: 'Absorptions- & Adsorptionskältemaschinen',
    body: 'Thermisch angetriebene Kälteerzeugung'
  },
  {
    id: 'block_intro',
    heading: 'Einführung',
    body:
      'Neben den elektrisch angetriebenen Kompressionskältemaschinen gibt es thermisch angetriebene Alternativen: Absorptions- und Adsorptionskältemaschinen. ' +
      'Sie nutzen Wärmeenergie (z.B. Abwärme, Solarthermie) als Hauptantriebsquelle und kommen oft dann zum Einsatz, wenn günstige Wärme verfügbar ist oder elektrische Energie gespart werden soll. ' +
      'Beide basieren auf dem Prinzip der Sorption, unterscheiden sich aber im Detail des Prozesses.'
  },
  {
    id: 'block_akm_titel',
    heading: 'Absorptionskältemaschinen (AKM)',
    body: 'Funktionsprinzip der AKM'
  },
  {
    id: 'block_akm_funktion',
    heading: 'AKM – Funktionsweise',
    body:
      'Absorptionskältemaschinen verwenden ein binäres Stoffgemisch bestehend aus einem Kältemittel und einem Lösungsmittel (Absorbens). ' +
      'Gängige Paare sind Wasser (Kältemittel) / Lithiumbromid (Lösungsmittel) für Klimaanwendungen (>0°C) oder Ammoniak (Kältemittel) / Wasser (Lösungsmittel) für tiefere Temperaturen.\n' +
      'Der Prozess ersetzt den mechanischen Verdichter einer Kompressionskältemaschine durch einen thermischen Verdichter, bestehend aus Austreiber, Lösungspumpe und Absorber.\n\n' +
      'Die Hauptkomponenten und der Kreislauf:\n\n' +
      '1. **Verdampfer:** Das Kältemittel (z.B. Wasser) verdampft bei niedrigem Druck und nimmt dabei Wärme aus dem zu kühlenden Medium auf (Kälteerzeugung).\n\n' +
      '2. **Absorber:** Der Kältemitteldampf strömt in den Absorber und wird dort vom konzentrierten Lösungsmittel (z.B. Lithiumbromid-Lösung) absorbiert. Dabei wird Wärme frei (Absorptionswärme), die abgeführt werden muss.\n\n' +
      '3. **Lösungspumpe:** Die nun verdünnte, kältemittelreiche Lösung wird von der Lösungspumpe auf ein höheres Druckniveau in den Austreiber gefördert. Dies ist der einzige nennenswerte elektrische Energieaufwand.\n\n' +
      '4. **Austreiber (Generator):** Durch Wärmezufuhr (Antriebswärme, z.B. Heißwasser, Dampf, Abgas) wird das Kältemittel aus der Lösung ausgetrieben (desorbiert). ' +
      'Der Kältemitteldampf strömt zum Verflüssiger, die konzentrierte Lösung zurück zum Absorber (über ein Drosselventil und oft einen Lösungswärmetauscher zur Effizienzsteigerung).\n\n' +
      '5. **Verflüssiger (Kondensator):** Der Kältemitteldampf aus dem Austreiber gibt Wärme an die Umgebung ab und kondensiert. Die Kondensationswärme muss abgeführt werden.\n\n' +
      '6. **Expansionsventil (Drossel):** Das flüssige Kältemittel wird auf den niedrigen Verdampferdruck entspannt und der Kreislauf beginnt von neuem.'
  },
  {
    id: 'block_adkm_titel',
    heading: 'Adsorptionskältemaschinen (ADKM)',
    body: 'Funktionsprinzip der ADKM'
  },
  {
    id: 'block_adkm_funktion',
    heading: 'ADKM – Funktionsweise',
    body:
      'Adsorptionskältemaschinen nutzen ein festes Sorptionsmittel (Adsorbens, z.B. Silicagel, Zeolith) und ein Kältemittel (meist Wasser). ' +
      'Im Gegensatz zur Absorption, bei der das Kältemittel im Lösungsmittel gelöst wird, lagert sich bei der Adsorption das Kältemittel an der Oberfläche des festen Sorptionsmittels an.\n' +
      'Der Prozess läuft diskontinuierlich in mindestens zwei Kammern ab, die abwechselnd ad- und desorbieren, um einen quasi-kontinuierlichen Kältebetrieb zu ermöglichen.\n\n' +
      'Die Phasen in einer Kammer:\n\n' +
      '1. **Adsorptionsphase (Kälteerzeugung):**\n' +
      '   • **Verdampfer:** Das Kältemittel (Wasser) verdampft bei niedrigem Druck und entzieht dem Kältekreislauf Wärme.\n' +
      '   • **Adsorberkammer:** Der Kältemitteldampf strömt in die Adsorberkammer, die mit dem kalten, trockenen Adsorbens gefüllt ist. ' +
      'Das Kältemittel wird am Adsorbens angelagert (adsorbiert). Dabei wird Adsorptionswärme frei, die abgeführt werden muss, um den Prozess aufrechtzuerhalten.\n\n' +
      '2. **Desorptionsphase (Regeneration des Adsorbens):**\n' +
      '   • **Adsorberkammer:** Die nun mit Kältemittel beladene Adsorberkammer wird von der Verdampferseite getrennt und mit einer Wärmequelle (Antriebswärme) verbunden. ' +
      'Durch die Wärmezufuhr wird das Kältemittel vom Adsorbens wieder ausgetrieben (desorbiert).\n' +
      '   • **Verflüssiger:** Der ausgetriebene Kältemitteldampf strömt in den Verflüssiger, wo er unter Wärmeabgabe kondensiert.\n' +
      '   • Nach der Desorption und Abkühlung ist das Adsorbens wieder bereit für die nächste Adsorptionsphase.\n\n' +
      'Um einen kontinuierlichen Kältebetrieb zu ermöglichen, arbeiten mindestens zwei solcher Adsorberkammern im Wechselbetrieb (eine adsorbiert, während die andere desorbiert und umgekehrt).'
  },
  {
    id: 'block_glt',
    heading: 'Anbindung an die Gebäudeleittechnik (GLT)',
    body:
      'Auch Sorptionskältemaschinen können in GLT-Systeme eingebunden werden. Die Anbindung erfolgt oft über:\n' +
      '• **Digitale Signale:** Freigabe Ein/Aus, Betriebsmeldung, Störmeldung.\n' +
      '• **Analoge Signale:** Sollwertvorgabe für die Kaltwassertemperatur, Ist-Temperaturen (Kaltwasser, Antriebswärme, Kühlwasser).\n' +
      '• **Bussysteme (Modbus, BACnet etc.):** Für einen umfangreicheren Datenaustausch, detaillierte Statusinformationen, Parametrierung und erweiterte Regelungsstrategien ' +
      '(z.B. Anpassung an verfügbare Antriebswärme).\n\n' +
      'Die Steuerung fokussiert sich auf die Regelung der Antriebswärmezufuhr und der Kühlwasser-/Rückkühlkreisläufe, um eine optimale Kälteleistung und Effizienz zu erzielen.'
  },
  {
    id: 'block_fazit',
    heading: 'Fazit',
    body:
      'Absorptions- und Adsorptionskältemaschinen bieten eine energieeffiziente Alternative zur Kälteerzeugung, insbesondere wenn Abwärme oder andere thermische Energiequellen kostengünstig zur Verfügung stehen. ' +
      'Ihre Integration in die GLT ermöglicht eine optimierte Betriebsweise und Nutzung im Gesamtkontext der Gebäudetechnik.'
  }
];

const M3_U1_X3_Absorp: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM3, canUploadImagesM3 } = useAuth();
  const canEditTexts = canEditTextsM3;
  const canUploadImages = canUploadImagesM3;
  const { override, refresh } = useUnitOverride(PAGE_ID);

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const mergedBlocks = React.useMemo(() => 
    textBlocks.map(tb => ({
      ...tb,
      body: override?.texts?.[tb.id] || tb.body
    })),
    [override]
  );

  const fullText = React.useMemo(() => mergedBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [mergedBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullText });

  const handleTextSave = React.useCallback(async (blockId: string, body: string) => {
    if (!token) return alert('Kein Token.');
    if (!canEditTexts) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: body } }, token);
      alert('Text gespeichert.');
      await refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canEditTexts]);

  const handleImageReplace = React.useCallback(async (key: string, file: File) => {
    if (!token) return alert('Kein Token.');
    if (!canUploadImages) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try {
      await uploadUnitAsset(PAGE_ID, key, file, token);
      alert('Bild aktualisiert.');
      await refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canUploadImages]);

  const ttsBtn = React.createElement(IconButton, { onClick: speaking ? stop : speak, 'aria-label': 'Vorlesen', sx: { ml: 1 } }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '3.1.3 Absorptions- & Adsorptionskälte', actions: ttsBtn },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 2 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        images.map(img => React.createElement(ZoomableImage, {
          key: img.key,
          src: imgSrc(img.key, img.src),
          alt: img.alt,
          label: 'Bild einfügen',
          onReplace: canUploadImages ? (file: File) => handleImageReplace(img.key, file) : undefined
        }))
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        mergedBlocks.map(tb => React.createElement(EditableTextBlock, {
          key: tb.id,
          pageId: PAGE_ID,
          blockId: tb.id,
          heading: tb.heading,
          body: tb.body,
          onSave: (body: string) => handleTextSave(tb.id, body)
        })),
        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
          React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m3_u1_x99_quiz') }, 'Zum Quiz')
        )
      )
    ),
    React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } })
  );
};

export default M3_U1_X3_Absorp;

